import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import type { PatientConsentFormData } from '@/services/patientConsent.service';
import { fetchConsentStatus, submitPatientConsent } from '@/services/patientConsent.service';
import { Logger } from '@/utils/logger';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';

const logger = new Logger('usePatientConsentStatus');

export const usePatientConsentStatus = (setPatientConsentSubmitted?: Dispatch<SetStateAction<boolean>>) => {
  const { data: user } = useUser();
  const queryClient = useQueryClient();

  const [patientConsentRequired, setPatientConsentRequired] = useState(true);

  const { isLoading, error, data } = useQuery({
    enabled: Boolean(user?.id),
    queryKey: ['patientConsentStatus'],
    queryFn: async () => {
      try {
        return await fetchConsentStatus(user!.id);
      } catch (err) {
        // Submitting consent is required in order to use the app.
        // If the API call fails, require the user to reload.
        logger.error('Call to fetch consent status failed', err);
        return Promise.resolve({
          requiresConsentSubmission: true,
          pendingConsentUuid: null,
          requireReload: true,
        });
      }
    },
  });

  const submitConsentMutation = useMutation({
    mutationFn: (formData: PatientConsentFormData) =>
      submitPatientConsent({
        ...formData,
        uuid: data?.pendingConsentUuid || null,
        userId: user?.id as number,
      }),
    onSuccess: async (res) => {
      queryClient.invalidateQueries({
        queryKey: ['patientConsentStatus'],
      });
      setPatientConsentRequired(!res?.data.succeeded);
      if (setPatientConsentSubmitted) {
        setPatientConsentSubmitted(true);
      }
    },
  });

  if (error) {
    return {
      isLoading: false,
      data: {
        requiresConsentSubmission: true,
        pendingConsentUuid: null,
        requireReload: true,
      },
      submitConsent: submitConsentMutation.mutateAsync,
    };
  }

  return {
    isLoading,
    data: {
      requiresConsentSubmission: patientConsentRequired && data?.requiresConsentSubmission,
      requireReload: data?.requireReload || false,
    },
    submitConsent: submitConsentMutation.mutateAsync,
  };
};
